import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

type JsonLd = Record<string, unknown>;

type Props = {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  structuredData?: JsonLd | JsonLd[];
};

const SITE_NAME = 'Promociones Montosa';
const BASE_URL = 'https://promonsa.net';

const LOCALE_MAP: Record<'en' | 'es', string> = {
  en: 'en_US',
  es: 'es_ES',
};

const alternateLocale = (locale: 'en' | 'es') => (locale === 'es' ? 'en_US' : 'es_ES');

const toAbsoluteUrl = (value?: string) => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  const normalised = value.startsWith('/') ? value : `/${value}`;
  return `${BASE_URL}${normalised}`;
};

export default function Seo({
  title,
  description,
  canonicalPath = '/',
  image,
  type = 'website',
  noindex = false,
  structuredData,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const locale = LOCALE_MAP[lang];

  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} · Granada Real Estate`;
  const metaDescription =
    description?.trim() ||
    t('seo.defaultDescription', {
      defaultValue: 'Promociones Montosa curates distinctive homes and new developments across Granada, Spain.',
    });
  const canonicalUrl = toAbsoluteUrl(canonicalPath);
  const imageUrl = toAbsoluteUrl(image);

  const jsonLdArray = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>

      {lang && <html lang={lang} />}

      <link rel="canonical" href={canonicalUrl} />

      <meta name="description" content={metaDescription} data-react-helmet="true"/>
      <meta name="language" content={lang} data-react-helmet="true"/>
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} data-react-helmet="true"/>

      <meta property="og:site_name" content={SITE_NAME} data-react-helmet="true"/>
      <meta property="og:type" content={type} data-react-helmet="true"/>
      <meta property="og:title" content={fullTitle} data-react-helmet="true"/>
      <meta property="og:description" content={metaDescription} data-react-helmet="true"/>
      <meta property="og:url" content={canonicalUrl} data-react-helmet="true"/>
      <meta property="og:locale" content={locale} data-react-helmet="true"/>
      <meta property="og:locale:alternate" content={alternateLocale(lang)} data-react-helmet="true"/>
      {imageUrl && <meta property="og:image" content={imageUrl} data-react-helmet="true"/>}

      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} data-react-helmet="true"/>
      <meta name="twitter:title" content={fullTitle} data-react-helmet="true"/>
      <meta name="twitter:description" content={metaDescription} data-react-helmet="true"/>
      {imageUrl && <meta name="twitter:image" content={imageUrl} data-react-helmet="true"/>}

      {jsonLdArray?.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
